/**
 * Marketing → Rendimiento: types of gula_performance(), periods, rates and the
 * good / normal / low grade of each number. Pure (vitest).
 */

export type PerfPeriodKey = "7d" | "30d" | "90d" | "month" | "last_month";

export const PERF_PERIODS: { key: PerfPeriodKey; label: string }[] = [
  { key: "7d", label: "7 días" },
  { key: "30d", label: "30 días" },
  { key: "90d", label: "90 días" },
  { key: "month", label: "Este mes" },
  { key: "last_month", label: "Mes pasado" },
];

export interface DayRange {
  from: string;
  to: string;
}

const pad = (n: number) => String(n).padStart(2, "0");
export const isoDay = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const dayOf = (day: string) => new Date(`${day}T12:00:00`);
const shift = (day: string, days: number) => {
  const date = dayOf(day);
  date.setDate(date.getDate() + days);
  return isoDay(date);
};

/** Local (Madrid) days of a period, both ends included. */
export const perfRange = (key: PerfPeriodKey, now = new Date()): DayRange => {
  const today = isoDay(now);
  if (key === "month") return { from: `${today.slice(0, 8)}01`, to: today };
  if (key === "last_month") {
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { from: `${isoDay(end).slice(0, 8)}01`, to: isoDay(end) };
  }
  const days = key === "7d" ? 7 : key === "30d" ? 30 : 90;
  return { from: shift(today, -(days - 1)), to: today };
};

/** The period of the same length just before. */
export const previousRange = (range: DayRange): DayRange => {
  const days =
    Math.round(
      (dayOf(range.to).getTime() - dayOf(range.from).getTime()) / 864e5,
    ) + 1;
  return { from: shift(range.from, -days), to: shift(range.from, -1) };
};

// --- gula_performance() ---------------------------------------------------

export interface PerfSummary {
  leads: number;
  called: number;
  talked: number;
  sure: number;
  sold: number;
  revenue: number;
  lost: number;
  unreached: number;
  open: number;
  days_to_sale_median: number | null;
  attempts_to_talk_avg: number | null;
}

export interface PerfSpeed {
  daytime_leads: number;
  median_minutes: number | null;
  within_5: number;
  within_15: number;
  within_60: number;
  same_day: number;
  never_called: number;
  fast_leads: number;
  fast_sold: number;
  slow_leads: number;
  slow_sold: number;
}

export interface PerfActivity {
  calls: number;
  answered: number;
  no_answer: number;
  whatsapp_talks: number;
  active_days: number;
}

export interface PerfSales {
  orders: number;
  revenue: number;
  avg_ticket: number | null;
  with_maintenance: number;
  days_to_sale_median: number | null;
  by_method: Record<string, number>;
  by_source: Record<string, number>;
}

export interface PerfSegment {
  dim: string;
  key: string;
  leads: number;
  talked: number;
  sold: number;
  revenue: number;
}

export interface PerfSetter {
  id: number;
  name: string;
  admin: boolean;
  calls: number;
  answered: number;
  whatsapp_talks: number;
  leads_talked: number;
  active_days: number;
  sales: number;
  revenue: number;
  first_call_median_minutes: number | null;
}

export interface PerfSpend {
  total: number;
  impressions: number;
  clicks: number;
  meta_leads: number;
  synced_at: string | null;
  by_ad: { ad: string; spend: number; meta_leads: number }[];
}

export interface Performance {
  from: string;
  to: string;
  summary: PerfSummary;
  speed: PerfSpeed;
  activity: PerfActivity;
  hours: { hour: number; calls: number; answered: number }[];
  sales: PerfSales;
  segments: PerfSegment[];
  setters: PerfSetter[];
  lost: { reason: string; n: number }[];
  spend: PerfSpend | null;
  daily: { day: string; leads: number; sales: number; spend: number | null }[];
}

// --- rates and grades -----------------------------------------------------

/** Percentage with one decimal, or null when there is nothing to divide. */
export const rate = (part: number, total: number): number | null =>
  total > 0 ? Math.round((part / total) * 1000) / 10 : null;

export const ratio = (part: number, total: number): number | null =>
  total > 0 ? Math.round((part / total) * 100) / 100 : null;

export type Grade = "good" | "ok" | "low";

/**
 * Reference thresholds (orientative, form leads of a ~500 € product).
 * Keep in sync with the prompt in supabase/functions/performance_report/report.ts.
 */
export const BENCHMARKS = {
  contactRate: { low: 50, good: 70, higherIsBetter: true, unit: "%" },
  closeRate: { low: 3, good: 6, higherIsBetter: true, unit: "%" },
  closeTalked: { low: 8, good: 15, higherIsBetter: true, unit: "%" },
  unreachedRate: { low: 35, good: 20, higherIsBetter: false, unit: "%" },
  speedMinutes: { low: 60, good: 15, higherIsBetter: false, unit: " min" },
  within60: { low: 50, good: 80, higherIsBetter: true, unit: "%" },
  pickupRate: { low: 30, good: 50, higherIsBetter: true, unit: "%" },
  costShare: { low: 35, good: 20, higherIsBetter: false, unit: "%" },
  roas: { low: 3, good: 5, higherIsBetter: true, unit: "x" },
} as const;

export type Metric = keyof typeof BENCHMARKS;

export const grade = (metric: Metric, value: number | null): Grade | null => {
  if (value == null || !Number.isFinite(value)) return null;
  const { low, good, higherIsBetter } = BENCHMARKS[metric];
  if (higherIsBetter) {
    if (value >= good) return "good";
    return value < low ? "low" : "ok";
  }
  if (value <= good) return "good";
  return value > low ? "low" : "ok";
};

const fmt = (value: number) =>
  value.toLocaleString("es-ES", { maximumFractionDigits: 1 });

/** "Bueno ≥ 6 % · bajo < 3 %" */
export const benchmarkText = (metric: Metric): string => {
  const { low, good, higherIsBetter, unit } = BENCHMARKS[metric];
  return higherIsBetter
    ? `Bueno ≥ ${fmt(good)}${unit} · bajo < ${fmt(low)}${unit}`
    : `Bueno ≤ ${fmt(good)}${unit} · bajo > ${fmt(low)}${unit}`;
};

export const GRADE_LABEL: Record<Grade, string> = {
  good: "Bueno",
  ok: "Normal",
  low: "Bajo",
};

/** Change against the previous period: points for rates, % for counts. */
export const delta = (
  current: number | null,
  previous: number | null,
): number | null =>
  current == null || previous == null
    ? null
    : Math.round((current - previous) * 10) / 10;

/** Whether a change is good news for this metric. */
export const isImprovement = (metric: Metric, change: number): boolean =>
  BENCHMARKS[metric].higherIsBetter ? change > 0 : change < 0;

// --- derived numbers --------------------------------------------------------

export interface KeyRates {
  contactRate: number | null;
  closeRate: number | null;
  closeTalked: number | null;
  unreachedRate: number | null;
  speedMinutes: number | null;
  within60: number | null;
  pickupRate: number | null;
  costPerLead: number | null;
  costPerSale: number | null;
  costShare: number | null;
  roas: number | null;
}

export const keyRates = (perf: Performance): KeyRates => {
  const s = perf.summary;
  const spend = perf.spend?.total ?? null;
  const costPerSale =
    spend != null && s.sold > 0 ? Math.round(spend / s.sold) : null;
  const ticket = perf.sales.avg_ticket ?? (s.sold ? s.revenue / s.sold : null);
  return {
    contactRate: rate(s.talked, s.leads),
    closeRate: rate(s.sold, s.leads),
    closeTalked: rate(s.sold, s.talked),
    unreachedRate: rate(s.unreached, s.leads),
    speedMinutes: perf.speed.median_minutes,
    within60: rate(perf.speed.within_60, perf.speed.daytime_leads),
    pickupRate: rate(perf.activity.answered, perf.activity.calls),
    costPerLead:
      spend != null && s.leads > 0
        ? Math.round((spend / s.leads) * 100) / 100
        : null,
    costPerSale,
    costShare:
      costPerSale != null && ticket ? rate(costPerSale, Number(ticket)) : null,
    roas: spend ? ratio(s.revenue, spend) : null,
  };
};

/** Segments of one dimension, biggest first. */
export const segmentsOf = (perf: Performance, dim: string): PerfSegment[] =>
  perf.segments
    .filter((segment) => segment.dim === dim)
    .sort((a, b) => b.leads - a.leads);

export interface AdRow extends PerfSegment {
  spend: number | null;
  costPerLead: number | null;
  costPerSale: number | null;
}

/** Leads and sales of each ad, with its Meta spend when there is one. */
export const adRows = (perf: Performance): AdRow[] => {
  const spendByAd = new Map(
    (perf.spend?.by_ad ?? []).map((row) => [row.ad, Number(row.spend)]),
  );
  return segmentsOf(perf, "ad").map((segment) => {
    const spend = spendByAd.has(segment.key)
      ? (spendByAd.get(segment.key) as number)
      : null;
    return {
      ...segment,
      spend,
      costPerLead:
        spend != null && segment.leads
          ? Math.round((spend / segment.leads) * 100) / 100
          : null,
      costPerSale:
        spend != null && segment.sold ? Math.round(spend / segment.sold) : null,
    };
  });
};

/**
 * The newest leads of the period can still buy: a lead takes a median of a
 * couple of days to close. True when the period ends within that window.
 */
export const isMaturing = (perf: Performance, now = new Date()): boolean => {
  const wait = Math.max(3, Math.ceil(perf.summary.days_to_sale_median ?? 3));
  return (
    dayOf(perf.to).getTime() >= dayOf(isoDay(now)).getTime() - wait * 864e5
  );
};

export const euros = (
  value: number | null | undefined,
  decimals = 0,
): string =>
  value == null
    ? "—"
    : `${Number(value).toLocaleString("es-ES", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })} €`;

export const percent = (value: number | null): string =>
  value == null ? "—" : `${fmt(value)} %`;

export const minutes = (value: number | null): string => {
  if (value == null) return "—";
  if (value < 60) return `${Math.round(value)} min`;
  const hours = value / 60;
  return hours < 24 ? `${fmt(hours)} h` : `${fmt(hours / 24)} d`;
};
